import { HttpErrorResponse, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { BehaviorSubject, catchError, filter, switchMap, take, throwError } from 'rxjs';
import { AuthService, RefreshTokenRequest } from '@api/api';
import { ACCESS_TOKEN_KEY, AuthenticationService, REFRESH_TOKEN_KEY } from "@core/services/authentication.service";
import { DeveloperService } from "@core/services/developer.service";

const accessTokenSubject = new BehaviorSubject<string | null>(null);
let refreshInProgress = false;

export const RefreshTokenInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);
  const authenticationService = inject(AuthenticationService);
  const devService = inject(DeveloperService);
  const logId = Math.random().toString(36).slice(2,8);

  // ne interceptáljuk a refresh kérdést
  if (req.url.includes('/auth/refresh')) {
    return next(req);
  }

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      if (authenticationService.isDeveloper()) {
        devService.addLog(logId, `❌ ERROR for ${req.method} ${req.url} → Status: ${error.status}, Message: ${error.message}, Details: ${error.error?.message}`);
      }

      if (error.status !== 401) {
        return rethrow(error);
      }

      const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
      if (!refreshToken) {
        refreshInProgress = false;
        accessTokenSubject.next(null);
        authenticationService.logout();
        return rethrow(error);
      }

      // ha épp nincs folyamatban a refresh
      if (refreshInProgress) {
        // ha már zajlik a refresh -> várunk, amíg érkezik új token
        return accessTokenSubject.pipe(
          filter(token => token !== null),
          take(1),
          switchMap(() => next(addToken(req))),
          catchError(err => rethrow(err))
        );
      }

      refreshInProgress = true;
      accessTokenSubject.next(null);

      return authService.refresh({refresh_token: refreshToken} as RefreshTokenRequest).pipe(
        switchMap(response => {
          const accessToken = response.access_token!;
          const refreshToken = response.refresh_token!;
          localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
          localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
          accessTokenSubject.next(accessToken!);
          refreshInProgress = false;
          return next(addToken(req));
        }),
        catchError(err => {
          accessTokenSubject.next(null);
          authenticationService.logout();
          refreshInProgress = false;
          return rethrow(err);
        })
      );
    })
  );
};

function addToken<T>(req: HttpRequest<T>): HttpRequest<T> {
  const headers =
    req.headers
      .set('Authorization', `Bearer ${localStorage.getItem(ACCESS_TOKEN_KEY)}`)
  return req.clone({headers});
}

function rethrow(error: unknown) {
  return throwError(() => error);
}
